import { Stage } from "./types";
import { tokenStore } from "./services/tokenStore";

export const resolveStageFromHostname = (hostname: string): Stage => {
  if (hostname === "localhost" || hostname === "127.0.0.1") {
    return "dev";
  }
  return hostname.startsWith("dev-") ? "dev" : "prod";
};

const stage = resolveStageFromHostname(window.location.hostname);

const apiBaseByStage: Record<Stage, string> = {
  dev: "https://8p60ot8y7a.execute-api.us-east-2.amazonaws.com/api/v1",
  prod: "https://iqdxbpogz1.execute-api.us-east-1.amazonaws.com/api/v1"
};

const rcApiBaseByStage: Record<Stage, string> = {
  dev: "https://stage-api.bellomyonline.com",
  prod: "https://api.bellomyonline.com"
};

/** Research Cloud API base URL selected independently from the Panel Insights API. */
export const resolveRcApiBaseUrl = (hostname: string): string =>
  rcApiBaseByStage[resolveStageFromHostname(hostname)];

export const appConfig = {
  stage,
  apiBaseUrl: process.env.REACT_APP_API_BASE_URL || apiBaseByStage[stage],
  briAuthBaseUrl: process.env.REACT_APP_BRI_AUTH_BASE_URL || "https://auth.bellomyonline.com",
  briAuthApiBaseUrl: process.env.REACT_APP_BRI_API_BASE_URL || "https://authapi.bellomyonline.com",
  rcApiBaseUrl: process.env.REACT_APP_RC_API_BASE_URL || resolveRcApiBaseUrl(window.location.hostname),
  defaultAudience: process.env.REACT_APP_BRI_AUDIENCE || "panel"
};

const normalizeAudience = (audience: string): string => audience.trim().toUpperCase();

/** Audience from the logged-in user's JWT claims (preferred over the saved fallback). */
const getAudienceFromUserClaims = (): string | null => {
  const token = tokenStore.getToken();
  if (!token) {
    return null;
  }
  const claims = tokenStore.getCachedClaims(token);
  if (!claims) {
    return null;
  }
  const aud = claims.audienceName || claims.userAudience;
  return aud ? normalizeAudience(aud) : null;
};

const getAudienceFromHostname = (hostname: string): string | null => {
  const devMatch = hostname.match(/^dev-([a-z0-9-]+)-(?:insights|paneladmin)\.bellomyonline\.com$/i);
  if (devMatch?.[1]) {
    return normalizeAudience(devMatch[1]);
  }

  const prodMatch = hostname.match(/^([a-z0-9-]+)-(?:insights|paneladmin)\.bellomyonline\.com$/i);
  if (prodMatch?.[1]) {
    return normalizeAudience(prodMatch[1]);
  }

  return null;
};

const isLoggedIn = (): boolean => !!tokenStore.getToken();

/** Audience from the "audience"/"adnc" querystring params — only meaningful pre-login. */
const getAudienceFromQueryString = (): string | null => {
  const search = new URLSearchParams(window.location.search);
  const queryAudience = search.get("audience") || search.get("adnc");
  return queryAudience ? normalizeAudience(queryAudience) : null;
};

export const getAudience = (): string => {
  const hostAudience = getAudienceFromHostname(window.location.hostname);
  if (hostAudience) {
    localStorage.setItem("bri_saved_audience", hostAudience);
    return hostAudience;
  }

  // Querystring audience only takes effect while signed out; a logged-in session
  // keeps its current audience regardless of what's in the URL.
  if (!isLoggedIn()) {
    const queryAudience = getAudienceFromQueryString();
    if (queryAudience) {
      localStorage.setItem("bri_saved_audience", queryAudience);
      return queryAudience;
    }
  }

  const claimsAudience = getAudienceFromUserClaims();
  if (claimsAudience) {
    localStorage.setItem("bri_saved_audience", claimsAudience);
    return claimsAudience;
  }

  const savedAudience = localStorage.getItem("bri_saved_audience");
  if (savedAudience) {
    return normalizeAudience(savedAudience);
  }

  return normalizeAudience(appConfig.defaultAudience);
};

export const authEndpoints = {
  get loginUrl() {
    return appConfig.briAuthBaseUrl;
  },
  get verifyUrl() {
    return `${appConfig.briAuthApiBaseUrl}/verify?audience=${encodeURIComponent(getAudience())}`;
  },
  get refreshUrl() {
    return `${appConfig.briAuthApiBaseUrl}/refresh?audience=${encodeURIComponent(getAudience())}`;
  },
};
